import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@heroui/react';

import CollectionThumb from './CollectionThumb';
import Notice from '../../common/Notice';
import { SkeletonCard } from '../../common/Skeleton';
import { getSuggestedCollections } from '../../../services/topics.service';
import { messageOf, SUGGESTED } from '../../../i18n/el';

// **Προτεινόμενες συλλογές** (D48), στην αρχική οθόνη.
//
// ⚠ **ΔΕΝ ΕΙΝΑΙ ΣΥΣΤΑΣΗ ΜΕ ΜΟΝΤΕΛΟ, ΚΑΙ Η ΟΘΟΝΗ ΤΟ ΑΠΟΔΕΙΚΝΥΕΙ.** Το §3.6 ορίζει
// το σύστημα ως **στρώμα επιμέλειας**: καμία αυτόματη εκτίμηση συνάφειας. Η
// πρόταση εδώ είναι **τομή δύο συνόλων λέξεων που έγραψαν άνθρωποι** — τα
// ενδιαφέροντα που δήλωσε ο ίδιος ο χρήστης και οι ετικέτες ή το θέμα που έβαλαν
// μέλη των συλλογών.
//
// Ο **λόγος είναι πάντοτε ορατός** δίπλα σε κάθε πρόταση, και αυτό δεν είναι
// διακόσμηση: είναι η διαφορά ανάμεσα σε «σου προτείνουμε αυτό» και «αυτό
// ταιριάζει με τη λέξη Χ που έγραψες εσύ». *Πρόταση που δεν εξηγείται είναι
// πρόταση που ο χρήστης δεν μπορεί να διορθώσει.*
//
// **Δύο κενές καταστάσεις, δύο μηνύματα.** «Δεν βρέθηκε τίποτε» και «δεν έχεις
// δηλώσει ενδιαφέροντα» έχουν εντελώς διαφορετική επόμενη ενέργεια — η δεύτερη
// είναι ένα κλικ στο προφίλ. Το `meta.interestCount` του διακομιστή είναι που τις
// ξεχωρίζει· χωρίς αυτό η οθόνη θα έστελνε τους μισούς χρήστες να περιμένουν κάτι
// που δεν πρόκειται να έρθει (ΜΛΑ-03).

/** Η αιτιολόγηση μιας πρότασης, σε μία φράση. */
export function reasonText(suggestion) {
    if (!suggestion) return '';

    const parts = [];
    if (suggestion.topic) parts.push(SUGGESTED.byTopic(suggestion.topic));
    if (suggestion.tags?.length > 0) parts.push(SUGGESTED.byTags(suggestion.tags));
    return parts.join(' · ');
}

export default function SuggestedCollections() {
    const [items, setItems] = useState([]);
    const [interestCount, setInterestCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        getSuggestedCollections().then(
            ({ data, meta }) => {
                setItems(data);
                setInterestCount(meta?.interestCount ?? 0);
                setLoading(false);
            },
            (caught) => {
                setError(messageOf(caught));
                setLoading(false);
            }
        );
    }, []);

    // **Η κάρτα εξαφανίζεται σε σφάλμα**, και είναι απόφαση: οι προτάσεις είναι
    // διευκόλυνση, και μήνυμα σφάλματος για κάτι που ο χρήστης δεν ζήτησε θα τον
    // έστελνε να διορθώσει τη δουλειά του ενώ εκείνη λειτουργεί.
    if (error) return null;

    return (
        <Card>
            <Card.Header>
                <Card.Title>{SUGGESTED.title}</Card.Title>
            </Card.Header>

            <Card.Content className="flex flex-col gap-3">
                {loading && <SkeletonCard />}

                {!loading && interestCount === 0 && (
                    <Notice status="info" title={SUGGESTED.title}>
                        {SUGGESTED.noInterests}{' '}
                        <Link to="/profile" className="font-medium underline">
                            {SUGGESTED.noInterestsLink}
                        </Link>
                    </Notice>
                )}

                {!loading && !error && interestCount > 0 && items.length === 0 && (
                    <p className="py-6 text-center text-sm text-muted">{SUGGESTED.none}</p>
                )}

                {!loading && items.length > 0 && (
                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                        {items.map((item) => (
                            <div key={item.id} className="flex flex-col gap-1">
                                <CollectionThumb
                                    id={item.id}
                                    title={item.title}
                                    topic={item.topic}
                                    description={item.description}
                                    visibility={item.visibility}
                                    archived={item.archived}
                                    memberCount={item.memberCount}
                                    resourceCount={item.resourceCount}
                                />
                                {/* Ο λόγος **κάτω από την κάρτα** και όχι μέσα της: η
                                    κάρτα είναι η ίδια που χρησιμοποιεί ο κατάλογος, και
                                    δεύτερη παραλλαγή της θα ήταν δεύτερο σχέδιο για το
                                    ίδιο πράγμα. */}
                                <p className="px-1 text-xs text-muted">{reasonText(item.suggestion)}</p>
                            </div>
                        ))}
                    </div>
                )}
            </Card.Content>
        </Card>
    );
}
